import { CheckCircle2, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useFetcher } from 'react-router'
import { IncomeReassuranceModal } from '#app/components/screening/IncomeReassuranceModal'
import {
	ScreeningSectionCard,
	ScreeningStepContainer,
} from '#app/components/screening/ScreeningStepContainer'
import { LogoMarquee } from '#app/components/screening/VerifyIncomeStep'
import type { DemoFlinksSummary } from '#app/routes/resources+/demo-flinks'

/**
 * Connected-accounts summary shown after a successful demo verification — mirrors
 * the real Verify Income step's "Connected Accounts" card, which renders ONE card
 * per connected bank (not per sub-account). The Flinks sandbox returns several
 * sub-accounts (Operations, Products, Credits…), so we collapse them into a
 * single institution row and note the count instead of listing each one.
 */
function DemoConnectedAccounts({ summary }: { summary: DemoFlinksSummary }) {
	if (!summary.accounts || summary.accounts.length === 0) return null
	const count = summary.accounts.length
	return (
		<ScreeningSectionCard title="Connected Accounts">
			<div className="-mt-3">
				<div className="flex items-center gap-4">
					<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E8E4DF] bg-white">
						<CheckCircle2 className="h-5 w-5 text-green-600" />
					</div>
					<div className="min-w-0 flex-1">
						<p className="text-[15px] font-medium text-[#32302F]">
							{summary.institution || 'Bank Account'}
						</p>
						<p className="text-[13px] text-[#747474]">
							{count === 1 ? '1 account' : `${count} accounts`} connected
						</p>
						{summary.holderName && (
							<p className="text-[13px] text-[#747474]">{summary.holderName}</p>
						)}
					</div>
					<div className="shrink-0">
						<span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-[12px] font-medium text-green-700">
							Connected
						</span>
					</div>
				</div>
			</div>
		</ScreeningSectionCard>
	)
}

/**
 * Demo-only Verify Income step.
 *
 * Runs the REAL Flinks Connect iframe through the no-DB `/resources/demo-flinks`
 * endpoint (same as staging behavior), but persists nothing — no callback row,
 * no background income job. Next stays disabled until verified data returns.
 */
export function DemoVerifyIncomeStep({
	onNext,
	onBack,
	previousStep,
	isVerified = false,
	onVerified,
}: {
	onNext: () => void
	onBack?: () => void
	/** The step the user navigated here from — used to show the reassurance
	 * modal only on the forward Employment (4) → Verify Income (5) transition. */
	previousStep?: number
	/** Verified state lifted into the flow shell so it survives leaving and
	 * re-entering this step (the demo persists nothing, so the local fetcher
	 * state is lost on unmount). */
	isVerified?: boolean
	/** Called once when income is verified so the flow shell can remember it. */
	onVerified?: () => void
}) {
	const tokenFetcher = useFetcher<{ iframeSrc?: string; error?: string }>()
	const accountsFetcher = useFetcher<{
		summary?: DemoFlinksSummary
		error?: string
	}>()

	const iframeSrc = tokenFetcher.data?.iframeSrc
	const tokenError = tokenFetcher.data?.error
	const summary = accountsFetcher.data?.summary
	const accountsError = accountsFetcher.data?.error
	const fetchingAccounts =
		accountsFetcher.state !== 'idle' && !summary && !accountsError
	const connecting = tokenFetcher.state !== 'idle'

	// Keep the last good summary so the connected-accounts card survives the
	// account fetcher revalidating (React Router revalidates active fetchers when
	// the URL changes on "Next"), which would otherwise blank `summary` for a beat.
	const [storedSummary, setStoredSummary] = useState<DemoFlinksSummary | null>(
		null,
	)
	useEffect(() => {
		if (summary && !storedSummary) setStoredSummary(summary)
	}, [summary, storedSummary])
	const effectiveSummary = storedSummary ?? summary

	// `verified` drives the whole UI. It's true once Flinks returns a summary in
	// THIS mount, or if the flow shell already recorded a prior verification
	// (isVerified) — so returning to this step from Review shows success instead
	// of asking the user to connect their bank all over again. It LATCHES: once
	// verified we never fall back to the connect/connecting screens, so clicking
	// "Next" (which revalidates the fetcher) can't flash the first screen.
	const [verifiedLatched, setVerifiedLatched] = useState(false)
	useEffect(() => {
		if ((summary || isVerified) && !verifiedLatched) setVerifiedLatched(true)
	}, [summary, isVerified, verifiedLatched])
	const verified = verifiedLatched || Boolean(effectiveSummary) || isVerified

	// Tell the flow shell the moment income is verified, so the flag survives a
	// later unmount/remount of this step — then advance straight to Review
	// (skip the success screen / Next click).
	const notifiedRef = useRef(false)
	useEffect(() => {
		if (summary && !notifiedRef.current) {
			notifiedRef.current = true
			onVerified?.()
			onNext()
		}
	}, [summary, onVerified, onNext])

	// Lets the user close the full-screen Flinks window and return to the
	// connect prompt.
	const [iframeDismissed, setIframeDismissed] = useState(false)

	// The reassurance modal mirrors production: on the income step a specialist
	// "is about to call". It should show ONLY when the user arrives here by
	// finishing the Employment step (the forward 4 → 5 transition) — never when
	// they land back on this step from Review/edit. `cameFromEmployment` (derived
	// from the flow's previousStep) gates that; local state handles dismissal.
	const cameFromEmployment = previousStep === 4
	const [showReassurance, setShowReassurance] = useState(true)

	// One-shot guard so we only fetch accounts once, no matter which signal
	// (Flinks postMessage, our callback, or the iframe onLoad) fires first.
	const requestedRef = useRef(false)
	const accountsLoad = accountsFetcher.load
	const requestAccounts = (loginId: string | null | undefined) => {
		if (!loginId || requestedRef.current) return
		requestedRef.current = true
		accountsLoad(
			`/resources/demo-flinks?intent=accounts&loginId=${encodeURIComponent(loginId)}`,
		)
	}

	// Flinks Connect posts a message to the parent window on completion (and our
	// same-origin callback re-posts the loginId as a fallback). This is the
	// reliable path — reading the iframe URL only works same-origin.
	useEffect(() => {
		function onMessage(event: MessageEvent) {
			let data: unknown = event.data
			if (typeof data === 'string') {
				try {
					data = JSON.parse(data)
				} catch {
					return
				}
			}
			if (!data || typeof data !== 'object') return
			const loginId = (data as { loginId?: string }).loginId
			if (loginId && !requestedRef.current) {
				requestedRef.current = true
				accountsLoad(
					`/resources/demo-flinks?intent=accounts&loginId=${encodeURIComponent(loginId)}`,
				)
			}
		}
		window.addEventListener('message', onMessage)
		return () => window.removeEventListener('message', onMessage)
	}, [accountsLoad])

	const startConnect = () => {
		setIframeDismissed(false)
		tokenFetcher.load('/resources/demo-flinks?intent=token')
	}

	// Whether the full-screen Flinks window is currently showing.
	const showFlinksIframe =
		Boolean(iframeSrc) && !verified && !fetchingAccounts && !iframeDismissed

	// Manage the body class so the screening progress bar hides while the
	// full-screen Flinks window is open — same signal the real step uses.
	useEffect(() => {
		if (showFlinksIframe) {
			document.body.classList.add('flinks-iframe-open')
		} else {
			document.body.classList.remove('flinks-iframe-open')
		}
		return () => {
			document.body.classList.remove('flinks-iframe-open')
		}
	}, [showFlinksIframe])

	const handleIframeLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
		// Fallback: once Flinks redirects the iframe to our same-origin callback we
		// can read its URL directly for the loginId.
		try {
			const search = e.currentTarget.contentWindow?.location?.search
			if (!search) return
			requestAccounts(new URLSearchParams(search).get('loginId'))
		} catch {
			// Cross-origin Flinks frame — ignore until it lands on our callback.
		}
	}

	// ── Full-screen Flinks window (matches the real Verify Income step) ──────
	if (showFlinksIframe) {
		return (
			<div className="fixed inset-0 z-[100] min-h-screen w-full bg-white">
				<button
					type="button"
					onClick={() => setIframeDismissed(true)}
					className="absolute top-4 right-4 z-[101] flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 bg-white shadow-md transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-[#32302F] focus:ring-offset-2 focus:outline-none"
					aria-label="Close bank connection"
				>
					<X className="h-5 w-5 text-[#32302F]" />
				</button>

				<LogoMarquee className="bg-white" />

				<div className="w-full" style={{ height: 'calc(100vh - 87px)' }}>
					<iframe
						title="Flinks Connect Window"
						src={iframeSrc}
						onLoad={handleIframeLoad}
						style={{ width: '100%', height: '100%', border: 'none' }}
						allow="fullscreen"
						// Omit allow-top-navigation: on completion Flinks tries to redirect
						// the PARENT page, which would blow away the demo after the loginId
						// is already captured in-iframe. Blocking top navigation keeps us on
						// /demo so the summary can render.
						sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals"
					/>
				</div>
			</div>
		)
	}

	// Mirror the real Verify Income step: the connect prompt is the default view
	// and the container's primary "Verify Income" button drives the connection.
	const showConnectPrompt = !verified && !fetchingAccounts

	return (
		<>
			<IncomeReassuranceModal
				open={showReassurance && !verified && cameFromEmployment}
				onOpenChange={setShowReassurance}
			/>
			<ScreeningStepContainer
				title="Verify Income"
				des="You will be redirected to Flinks' Secure Portal"
				onNext={
					verified ? onNext : showConnectPrompt ? startConnect : undefined
				}
				onBack={onBack}
				isSubmitting={connecting}
				canGoNext={verified || showConnectPrompt}
				nextButtonText={showConnectPrompt ? 'Verify Income' : undefined}
			>
				<div className="space-y-4 sm:space-y-6">
					{verified ? (
						<>
							{effectiveSummary && (
								<DemoConnectedAccounts summary={effectiveSummary} />
							)}
							<ScreeningSectionCard>
								<p className="flex items-center gap-2 text-[16px] text-green-600">
									<CheckCircle2 className="h-4 w-4 shrink-0" />
									Your information has been collected successfully. You can
									proceed to the next step.
								</p>
							</ScreeningSectionCard>
						</>
					) : fetchingAccounts ? (
						<ScreeningSectionCard title="">
							<div className="space-y-6">
								<div className="text-center">
									<p className="mb-4 text-[16px] text-[#484848]">
										Connecting to your bank...
									</p>
								</div>
								<LogoMarquee />
							</div>
						</ScreeningSectionCard>
					) : (
						<ScreeningSectionCard>
							<div className="space-y-4">
								{(tokenError || accountsError) && (
									<div className="rounded-lg border border-red-200 bg-red-50 p-4">
										<p className="text-[16px] text-red-600">
											{tokenError || accountsError} — please try again.
										</p>
									</div>
								)}
								<div className="flex items-start">
									<img
										src="/img/ListItemsIconGreen.png"
										alt="Info"
										className="mt-0.5 mr-2 inline-block"
									/>
									<p className="text-[16px] text-[#32302F]">
										<span className="font-[500]">
											BANKING PROFILE MUST BELONG TO YOU
										</span>{' '}
										( Do not complete this process on behalf of someone else).
									</p>
								</div>
								<div className="flex items-start">
									<img
										src="/img/ListItemsIconGreen.png"
										alt="Info"
										className="mt-0.5 mr-2 inline-block"
									/>
									<p className="text-[16px] text-[#32302F]">
										Instant Verification
									</p>
								</div>
								<div className="flex items-start">
									<img
										src="/img/ListItemsIconGreen.png"
										alt="Info"
										className="mt-0.5 mr-2 inline-block"
									/>
									<p className="text-[16px] text-[#32302F]">
										Bank-Level Security
									</p>
								</div>
							</div>
						</ScreeningSectionCard>
					)}
				</div>
			</ScreeningStepContainer>
		</>
	)
}
